import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { UserCoordinates, LocationType, NewStatusAnimation } from '../types';
import apiService from '../services/api';
import { getErrorMessage } from '../utils/errors';
import { MarkerArrivalEffect, markerAnimationDuration, randomMarkerArrivalEffect } from '../utils/markerArrivalEffects';

interface LocationState {
  userCoordinates: UserCoordinates | null;
  allUserCoordinates: UserCoordinates[];
  collectedEmojis: Set<string>;
  isLoading: boolean;
  error: string | null;
  isManualLocationActive: boolean;
  animatedLocationEffects: Map<number, MarkerArrivalEffect>;

  // Actions
  updateUserLocation: (latitude: number, longitude: number, type?: LocationType) => Promise<void>;
  fetchAllCoordinates: () => Promise<void>;
  fetchUserLocation: () => Promise<void>;
  clearError: () => void;
  loadUserCoordinatesFromLocalStorage: () => UserCoordinates | null;
  saveUserCoordinatesToLocalStorage: (coords: UserCoordinates) => void;
  requestCurrentLocation: () => Promise<void>;
  addCollectedEmojis: (emojis: string[]) => void;
  upsertUserCoordinate: (coordinate: UserCoordinates, animation?: NewStatusAnimation) => void;
  stopLocationAnimation: (userId: number) => void;
}

export const useLocationStore = create<LocationState>()(
  subscribeWithSelector((set, get) => {
  const animationTimers = new Map<number, ReturnType<typeof setTimeout>>();
  const clearAnimationTimer = (userId: number) => {
    const timer = animationTimers.get(userId);
    if (timer) clearTimeout(timer);
    animationTimers.delete(userId);
  };

  return ({
  userCoordinates: null,
  allUserCoordinates: [],
  collectedEmojis: new Set(),
  isLoading: false,
  error: null,
  isManualLocationActive: true,
  animatedLocationEffects: new Map(),

  updateUserLocation: async (latitude: number, longitude: number, type?: LocationType) => {
    set({ isLoading: true, error: null });
    try {
      const coordinates = await apiService.updateCoordinates({ latitude, longitude, type });
      // Validate coordinates before setting
      if (coordinates && typeof coordinates.latitude === 'number' && typeof coordinates.longitude === 'number' &&
          !isNaN(coordinates.latitude) && !isNaN(coordinates.longitude)) {
        set({
          userCoordinates: coordinates,
          isLoading: false,
          error: null,
          isManualLocationActive: type === LocationType.MANUAL
        });
        // Save to localStorage after successful update
        get().saveUserCoordinatesToLocalStorage(coordinates);
      } else {
        throw new Error('Invalid coordinates received from server');
      }
    } catch (error: unknown) {
      set({
        isLoading: false,
        error: getErrorMessage(error, 'Failed to update location')
      });
      throw error;
    }
  },

  fetchUserLocation: async () => {
    set({ isLoading: true, error: null });
    try {
      // First, try to load from localStorage as initial position
      const localCoords = get().loadUserCoordinatesFromLocalStorage();
      if (localCoords) {
        set({
          userCoordinates: localCoords,
          isLoading: false,
          error: null
        });
      }

      // Fetch this user's location directly rather than searching a capped global list.
      const userCoords = await apiService.getCurrentCoordinates();
      if (userCoords) {
        set({
          userCoordinates: userCoords,
          isLoading: false,
          error: null,
          isManualLocationActive: userCoords.type === LocationType.MANUAL
        });
        get().saveUserCoordinatesToLocalStorage(userCoords);
        return;
      }

      // If no backend location found but we have localStorage, keep it
      if (!localCoords) {
        set({ isLoading: false });
      }
    } catch (error: unknown) {
      // If backend fails but we have localStorage, keep it
      const localCoords = get().loadUserCoordinatesFromLocalStorage();
      if (!localCoords) {
        set({
          isLoading: false,
          error: getErrorMessage(error, 'Failed to fetch user location')
        });
      } else {
        set({ isLoading: false });
      }
    }
  },

  fetchAllCoordinates: async () => {
    set({ isLoading: true, error: null });
    try {
      const coordinates = await apiService.getAllCoordinates();
      set({ allUserCoordinates: coordinates, isLoading: false, error: null });
      // Collect emojis
      const emojis = coordinates
        .map(c => c.user.avatarEmoji)
        .filter((e): e is string => typeof e === 'string' && e.trim() !== '');
      if (emojis.length > 0) {
        get().addCollectedEmojis(emojis);
      }
    } catch (error: unknown) {
      set({
        isLoading: false,
        error: getErrorMessage(error, 'Failed to fetch coordinates')
      });
    }
  },

  clearError: () => {
    set({ error: null });
  },

  loadUserCoordinatesFromLocalStorage: () => {
    try {
      const stored = localStorage.getItem('userCoordinates');
      if (stored) {
        const coords = JSON.parse(stored);
        // Validate the structure and ensure coordinates are valid numbers
        if (coords && typeof coords.latitude === 'number' && typeof coords.longitude === 'number' &&
            !isNaN(coords.latitude) && !isNaN(coords.longitude) &&
            coords.latitude >= -90 && coords.latitude <= 90 &&
            coords.longitude >= -180 && coords.longitude <= 180) {
          return coords as UserCoordinates;
        }
      }
    } catch (error) {
      console.error('Failed to load coordinates from localStorage:', error);
    }
    return null;
  },

  saveUserCoordinatesToLocalStorage: (coords: UserCoordinates) => {
    try {
      localStorage.setItem('userCoordinates', JSON.stringify(coords));
    } catch (error) {
      console.error('Failed to save coordinates to localStorage:', error);
    }
  },

  requestCurrentLocation: async () => {
    if (!navigator.geolocation) {
      throw new Error('Geolocation is not supported by this browser');
    }

    return new Promise<void>((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          try {
            await get().updateUserLocation(latitude, longitude, LocationType.BROWSER);
            resolve();
          } catch (error) {
            reject(error);
          }
        },
        (error) => {
          let errorMessage = 'Failed to get location';
          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorMessage = 'Location permission denied';
              break;
            case error.POSITION_UNAVAILABLE:
              errorMessage = 'Location unavailable';
              break;
            case error.TIMEOUT:
              errorMessage = 'Location request timed out';
              break;
          }
          reject(new Error(errorMessage));
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 30000
        }
      );
    });
  },

  // Add collected emojis
  addCollectedEmojis: (emojis: string[]) => {
    set(state => {
      const collectedEmojis = new Set(state.collectedEmojis);
      emojis.forEach(emoji => collectedEmojis.add(emoji));
      return { collectedEmojis };
    });
  },

  // Merge a live location update (e.g. from the WebSocket) into the visible set.
  // Replaces any existing entry for that user rather than appending a duplicate.
  upsertUserCoordinate: (coordinate: UserCoordinates, animation = NewStatusAnimation.TEN_SECONDS) => {
    clearAnimationTimer(coordinate.user.id);
    set((state) => {
      const withoutUser = state.allUserCoordinates.filter(
        (c) => c.user.id !== coordinate.user.id,
      );
      const animatedLocationEffects = new Map(state.animatedLocationEffects);
      if (animation === NewStatusAnimation.NEVER) animatedLocationEffects.delete(coordinate.user.id);
      else animatedLocationEffects.set(coordinate.user.id, randomMarkerArrivalEffect());
      return { allUserCoordinates: [...withoutUser, coordinate], animatedLocationEffects };
    });

    const duration = markerAnimationDuration(animation);
    if (duration !== null) {
      animationTimers.set(coordinate.user.id, setTimeout(() => {
        animationTimers.delete(coordinate.user.id);
        get().stopLocationAnimation(coordinate.user.id);
      }, duration));
    }
  },

  stopLocationAnimation: (userId: number) => {
    clearAnimationTimer(userId);
    set((state) => {
      if (!state.animatedLocationEffects.has(userId)) return state;
      const animatedLocationEffects = new Map(state.animatedLocationEffects);
      animatedLocationEffects.delete(userId);
      return { animatedLocationEffects };
    });
  }
})})
);

// Selectors for selective subscriptions
export const useUserCoordinates = () => useLocationStore(state => state.userCoordinates);
export const useAllUserCoordinates = () => useLocationStore(state => state.allUserCoordinates);
export const useLocationLoading = () => useLocationStore(state => state.isLoading);
export const useLocationError = () => useLocationStore(state => state.error);
export const useIsManualLocationActive = () => useLocationStore(state => state.isManualLocationActive);
